import React, { useState } from "react";
import { motion } from "framer-motion";
import { PACKAGES, EXTRAS, YEARS, priceFor } from "../data";
import { Check, Star, Moon, Anchor } from "lucide-react";

const Packages = ({ onReserve }) => {
  const [mode, setMode] = useState("dive"); // 'dive' or 'no-dive'
  const [year, setYear] = useState(YEARS[0]);

  return (
    <section id="pacotes" className="relative py-24 md:py-32" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="overline mb-3">Pacotes {year}</div>
          <h2 className="font-display text-4xl md:text-5xl font-light leading-tight max-w-3xl mx-auto">
            Escolha o ritmo da sua imersão.
          </h2>
          <p className="mt-4 text-base max-w-2xl mx-auto" style={{ color: "var(--ocean-muted)" }}>
            Saídas de 2, 3 e 4 dias. Para mergulhadores e não-mergulhadores. Crianças até 6 anos
            não pagam · até 12 anos pagam 50%.
          </p>
        </div>

        {/* Year tabs */}
        <p
          className="text-center text-lg md:text-xl mb-3"
          style={{ color: "var(--ocean-muted)" }}
          data-testid="packages-year-hint"
        >
          Escolha o ano e como você quer sua expedição
        </p>
        <div className="flex justify-center gap-2 mb-4" data-testid="packages-year-tabs">
          {YEARS.map((y) => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className="px-5 py-2 text-sm transition"
              style={{
                background: year === y ? "var(--ocean-accent)" : "transparent",
                color: year === y ? "#04111E" : "var(--ocean-muted)",
                border: `1px solid ${year === y ? "var(--ocean-accent)" : "var(--ocean-border)"}`,
              }}
              data-testid={`packages-year-tab-${y}`}
            >
              {y}
            </button>
          ))}
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex glass p-1" data-testid="pricing-toggle">
            <button
              className={`px-6 py-2.5 text-sm tracking-wider uppercase transition ${mode === "dive" ? "" : "opacity-50"}`}
              style={{ background: mode === "dive" ? "var(--ocean-primary)" : "transparent", color: mode === "dive" ? "#04111E" : "#fff", fontWeight: 600 }}
              onClick={() => setMode("dive")}
              data-testid="toggle-com-mergulho"
            >
              Com Mergulho
            </button>
            <button
              className={`px-6 py-2.5 text-sm tracking-wider uppercase transition ${mode === "no-dive" ? "" : "opacity-50"}`}
              style={{ background: mode === "no-dive" ? "var(--ocean-accent)" : "transparent", color: mode === "no-dive" ? "#04111E" : "#fff", fontWeight: 600 }}
              onClick={() => setMode("no-dive")}
              data-testid="toggle-sem-mergulho"
            >
              Sem Mergulho
            </button>
          </div>
        </div>

        {/* Night dive requirement notice — só aparece em Com Mergulho */}
        {mode === "dive" && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl mx-auto mb-12 space-y-3"
            data-testid="night-dive-notice"
          >
            <div
              className="text-center py-3 px-4"
              style={{
                background: "var(--ocean-primary)",
                color: "#04111E",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
              }}
              data-testid="certified-only-badge"
            >
              Pacote exclusivo para quem já é mergulhador certificado
            </div>
            <div
              className="flex items-start gap-3 p-4"
              style={{
                background: "rgba(245,199,84,0.08)",
                border: "1px solid rgba(245,199,84,0.35)",
              }}
            >
              <Moon size={18} style={{ color: "var(--ocean-accent)", marginTop: 2, flexShrink: 0 }} />
              <div className="text-xs sm:text-sm" style={{ color: "var(--ocean-muted)" }}>
                <strong style={{ color: "var(--ocean-accent)", fontWeight: 600 }}>Mergulhos noturnos:</strong>{" "}
                exigem certificação <strong style={{ color: "#fff" }}>Advanced Open Water</strong> (ou equivalente).
                Se você ainda é Open Water, pode fazer o Curso Avançado a bordo (R$ 1.800,00 — ver adicionais abaixo)
                ou o Batismo Noturno com lanterna (R$ 200,00, sem certificação prévia).
              </div>
            </div>
          </motion.div>
        )}

        {/* Sem Mergulho — convite pra experimentar */}
        {mode === "no-dive" && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl mx-auto mb-12 flex items-start gap-3 p-4"
            style={{
              background: "rgba(0,168,232,0.10)",
              border: "1px solid rgba(0,168,232,0.40)",
            }}
            data-testid="no-dive-notice"
          >
            <Anchor size={18} style={{ color: "var(--ocean-primary)", marginTop: 2, flexShrink: 0 }} />
            <div className="text-xs sm:text-sm" style={{ color: "var(--ocean-muted)" }}>
              <strong style={{ color: "var(--ocean-primary)", fontWeight: 600 }}>Nunca mergulhou? Sem problema.</strong>{" "}
              Nesses pacotes você pode experimentar com um <strong style={{ color: "#fff" }}>Batismo em Abrolhos</strong>{" "}
              (R$ 300,00 diurno · R$ 200,00 noturno, com instrutor e sem certificação prévia) ou virar mergulhador certificado
              fazendo o <strong style={{ color: "#fff" }}>Curso Open Water a bordo</strong> (R$ 1.800,00) — veja adicionais abaixo.
            </div>
          </motion.div>
        )}

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {PACKAGES.map((pkg, idx) => {
            const data = mode === "dive" ? pkg.withDive : pkg.withoutDive;
            const isHighlight = pkg.highlight;
            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                className={`relative glass p-8 lift ${isHighlight ? "md:-translate-y-4" : ""}`}
                style={{
                  borderColor: isHighlight ? "var(--ocean-accent)" : "var(--ocean-border)",
                  borderWidth: isHighlight ? "1.5px" : "1px",
                }}
                data-testid={`package-card-${pkg.id}`}
              >
                {isHighlight && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[0.65rem] tracking-[0.2em] uppercase flex items-center gap-1.5"
                    style={{ background: "var(--ocean-accent)", color: "#04111E", fontWeight: 600 }}
                  >
                    <Star size={11} fill="#04111E" /> {pkg.badge}
                  </div>
                )}
                {!isHighlight && (
                  <div className="overline mb-2" style={{ color: "var(--ocean-muted)" }}>
                    {pkg.badge}
                  </div>
                )}

                <div className="font-display text-5xl font-light leading-none">{pkg.duration}</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ocean-muted)" }}>
                  {pkg.nights}
                </div>

                <div className="my-8 flex items-baseline gap-1">
                  <span className="text-sm" style={{ color: "var(--ocean-muted)" }}>R$</span>
                  <span className="font-display text-5xl font-light" data-testid={`price-${pkg.id}`}>
                    {priceFor(pkg, mode, year).toLocaleString("pt-BR")}
                  </span>
                  <span className="text-sm ml-1" style={{ color: "var(--ocean-muted)" }}>
                    /pessoa
                  </span>
                </div>

                <div className="divider-grad mb-6" />

                <ul className="space-y-3 text-sm mb-8">
                  <li className="flex items-start gap-2">
                    <Check size={15} style={{ color: "var(--ocean-accent)", marginTop: 2 }} />
                    <span>
                      {mode === "dive" ? data.dives : data.activities}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={15} style={{ color: "var(--ocean-accent)", marginTop: 2 }} />
                    <span>Todas as refeições inclusas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={15} style={{ color: "var(--ocean-accent)", marginTop: 2 }} />
                    <span>Pernoite a bordo do ZEUS</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={15} style={{ color: "var(--ocean-accent)", marginTop: 2 }} />
                    <span>Visita à Ilha de Santa Bárbara</span>
                  </li>
                </ul>

                <div className="text-xs mb-6 pb-6 border-b" style={{ color: "var(--ocean-muted)", borderColor: "var(--ocean-border)" }}>
                  + Ingresso ICMBio R$ {data.icmbio},00 (estrangeiros R$ {data.icmbioForeign},00)
                </div>

                <button
                  onClick={() =>
                    onReserve(`${pkg.duration} · ${pkg.nights} · ${mode === "dive" ? "Com Mergulho" : "Sem Mergulho"}`)
                  }
                  className={isHighlight ? "btn-primary w-full justify-center" : "btn-ghost w-full justify-center"}
                  data-testid={`reserve-btn-${pkg.id}`}
                >
                  Reservar essa saída
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Extras */}
        <div className="mt-16 glass p-8">
          <div className="overline mb-6">Adicionais opcionais</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {EXTRAS.map((e) => (
              <div key={e.label}>
                <div className="text-xs uppercase tracking-wider mb-1" style={{ color: "var(--ocean-muted)" }}>
                  {e.label}
                </div>
                <div className="font-display text-xl" style={{ color: "var(--ocean-accent)" }}>{e.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Packages;